import '@mui/x-tree-view/themeAugmentation'
import { Components, Theme } from '@mui/material'
import { ChevronDownOMIcon, ChevronRightOMIcon } from '@omnea-app/icons'
import { iconButtonBoxSizes } from './iconButtonStyles'
import { menuItemIconStyle } from './MuiMenu'

declare module '@mui/x-tree-view/TreeItem' {
  interface TreeItemProps {
    /** Marks a row whose record the viewer may not open; the row stops inviting a click. */
    'data-restricted'?: boolean
    /**
     * @deprecated The 12px chevron trees used before chevrons took a small icon button's box. Kept only so an existing
     * tree doesn't change until it moves to the default size; don't use it in new trees.
     */
    'data-small-chevron'?: boolean
  }
}

const CHEVRON_SIZE_PX = 18

// The box of a small icon button, so a chevron's tint matches the row's other buttons.
const ICON_SLOT = iconButtonBoxSizes.small
const SMALL_CHEVRON_PX = 12

// The line runs down the parent's icon, and a child's icon sits under its parent's label.
const groupIndent = (theme: Theme, slot: string) => ({
  marginLeft: `calc(${theme.spacing(2.5)} + ${slot} / 2 - ${theme.strokeWidth.strong} / 2)`,
  paddingLeft: `calc(${slot} / 2 + ${theme.spacing(2)} - ${theme.spacing(2.5)} - ${theme.strokeWidth.strong} / 2)`,
})

const TreeItemCollapseIcon = () => <ChevronDownOMIcon size={`${CHEVRON_SIZE_PX}px`} />

const TreeItemExpandIcon = () => <ChevronRightOMIcon size={`${CHEVRON_SIZE_PX}px`} />

export const MuiTreeItem: Components<Omit<Theme, 'components'>>['MuiTreeItem'] = {
  defaultProps: {
    slots: {
      collapseIcon: TreeItemCollapseIcon,
      expandIcon: TreeItemExpandIcon,
    },
  },
  variants: [
    {
      // A restricted node is shown by its position only and cannot be opened, so it must not invite a click.
      props: { 'data-restricted': true },
      style: {
        '& > .MuiTreeItem-content': {
          cursor: 'default',
          '&:hover': {
            backgroundColor: 'transparent',
          },
        },
        '&:focus-visible > .MuiTreeItem-content': {
          backgroundColor: 'transparent',
        },
      },
    },
    {
      // The deprecated small chevron: the glyph and its slot at 12px, indenting to match.
      props: { 'data-small-chevron': true },
      style: ({ theme }: { theme: Theme }) => {
        const slot = `${SMALL_CHEVRON_PX}px`
        return {
          '& > .MuiTreeItem-content > .MuiTreeItem-iconContainer': {
            width: slot,
            minWidth: slot,
            height: slot,
            '& svg': { width: slot, height: slot },
          },
          '& > .MuiTreeItem-groupTransition': groupIndent(theme, slot),
        }
      },
    },
  ],
  styleOverrides: {
    root: ({ theme }) => ({
      // The tree focuses a row on click too; only keyboard focus tints it, so a clicked row clears once the
      // pointer leaves.
      '&:focus-visible > .MuiTreeItem-content': {
        backgroundColor: theme.palette.bg.interactive.hover,
      },
      // Only a row that opens tints its chevron, on top of the row's own tint; a leaf's icon does nothing.
      '&[aria-expanded] > .MuiTreeItem-content > .MuiTreeItem-iconContainer:hover': {
        backgroundColor: theme.palette.bg.interactive.hover,
        borderRadius: theme.shape['radius-md'],
      },
    }),
    // The whole row tints, its icon included.
    content: ({ theme }) => ({
      padding: theme.spacing(1.5, 2.5),
      marginBottom: theme.spacing(1),
      gap: theme.spacing(2),
      borderRadius: theme.shape['radius-md'],
      cursor: 'pointer',
      // MUI tints a focused row, and a click focuses one, so its tint would stay after the pointer leaves.
      '&.Mui-focused': {
        backgroundColor: 'transparent',
      },
      '&:hover': {
        backgroundColor: theme.palette.bg.interactive.hover,
      },
      '&.Mui-selected, &.Mui-selected.Mui-focused': {
        backgroundColor: theme.palette.bg.neutral.muted,
      },
      '&.Mui-selected:hover': {
        backgroundColor: theme.palette.bg.neutral.mutedHover,
      },
    }),
    label: {
      padding: 0,
    },
    // The chevron of a row that opens, or the icon of one that doesn't, drawn like a menu row's icon and held
    // in a small icon button's box. The row's gap already spaces it from the label.
    iconContainer: ({ theme }) => ({
      ...menuItemIconStyle(theme),
      width: ICON_SLOT,
      minWidth: ICON_SLOT,
      height: ICON_SLOT,
      marginRight: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }),
    groupTransition: ({ theme }) => ({
      ...groupIndent(theme, ICON_SLOT),
      borderLeft: `${theme.strokeWidth.strong} solid ${theme.palette.border.subtle}`,
    }),
  },
}
